import moment from 'moment';
import { FC, useMemo, useState } from 'react';

import { useAgencyConstants } from '@core/contexts/ConfigContext';
import { useOptions } from '@core/contexts/OptionsContext';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { DatePicker, MaterialDate } from '@shared/components/date-picker';
import { Flex } from '@shared/components/flex';
import { MultiGroupSelect } from '@shared/components/group-select-new/multi-group-select';
import { SingleGroupSelect } from '@shared/components/group-select-new/single-group-select';
import { NumberField } from '@shared/components/number-field';
import { RadioGroup, RadioGroupSerializerType } from '@shared/components/radio-group';
import { Option } from '@shared/components/select-new/Select';
import { SingleSelect } from '@shared/components/select-new/single-select';
import { UserAutocomplete } from '@shared/components/user-autocomplete';
import { getInclusiveRadioOptions } from '@shared/constants/inclusiveRadioOptions';
import { useCoreOptions } from '@shared/contexts/OptionsContext';
import { ButtonSize } from '@shared/types/common/button';
import { LookupUserItem } from '@shared/types/common/common';
import { InputSize } from '@shared/types/common/input';

import { styles } from './LettingDetailsForm.styles';

export interface LettingDetailsFormData {
  assignee: LookupUserItem | undefined;
  leaseEnd: MaterialDate;
  leaseEndReadable: string;
  leaseLengthDays: string;
  leaseLengthMonths: string;
  leaseLengthYears: string;
  leaseStart: MaterialDate;
  sectors: string[];
  source: string;
  rent: string;
  rentCurrency: string;
  rentMetric: Metric;
  rentRates: string;
  transactionDate: MaterialDate;
  transactionType: string | number;
}

export interface LettingDetailsFormProps extends WithStyles<typeof styles> {
  data: LettingDetailsFormData;
  errors?: Record<string, boolean>;
  lettingTypeOptions?: Option[];
  notInSchedule: boolean;
  withRentRates?: boolean;
  updateData: (data: Partial<LettingDetailsFormData>) => void;
}

const LettingDetailsFormComponent: FC<LettingDetailsFormProps> = ({
  classes,
  data,
  errors,
  lettingTypeOptions = [],
  notInSchedule,
  withRentRates = true,
  updateData,
}) => {
  const inclusiveRadioOptions = useMemo(() => getInclusiveRadioOptions(), []);

  const { constants } = useAgencyConstants();
  const { currencyOptions } = useOptions();
  const { groupedPreferredSectorOptions, groupedPreferredSourceOptions, rentMetricsOptions } = useCoreOptions();

  const [changeAssignee, setChangeAssignee] = useState<boolean>(false);

  const calculateLeaseEnd = (data: LettingDetailsFormData) => {
    if (data.leaseStart) {
      const date = moment(data.leaseStart);

      if (data.leaseLengthYears) date.add(data.leaseLengthYears, 'years' as moment.unitOfTime.DurationConstructor);
      if (data.leaseLengthMonths) date.add(data.leaseLengthMonths, 'months' as moment.unitOfTime.DurationConstructor);
      if (data.leaseLengthDays) date.add(data.leaseLengthDays, 'days' as moment.unitOfTime.DurationConstructor);

      const dateYears = date.diff(moment(data.leaseStart), 'years');

      if (dateYears) {
        date.add(-1, 'days');
      }

      const dateDays = date.diff(moment(data.leaseStart), 'days');

      if (dateDays) {
        return {
          date: date,
          dateReadable: date.format('Do MMMM YYYY'),
        };
      }

      return {
        date: null,
        dateReadable: '',
      };
    }

    return {
      date: null,
      dateReadable: '',
    };
  };

  const leaseEndDate = useMemo(() => {
    if (data.leaseEndReadable) {
      return t('lease_end_colon_xleaseendx', {
        lease_end: data.leaseEndReadable,
      });
    }

    return t('lease_end_colon_xleaseendx', {
      lease_end: t('xtextx_ellipsis', { text: t('please_enter_lease_start_and_length') }),
    });
  }, [data.leaseEndReadable]);

  const addDate = (dateType: keyof LettingDetailsFormData) => (value: string) => {
    const calculatedEnd = calculateLeaseEnd({ ...data, [dateType]: value });
    updateData({
      [dateType]: value,
      leaseEndReadable: calculatedEnd.dateReadable,
      leaseEnd: calculatedEnd.date,
    });
  };

  const handleChangeEnterRent = (value: string) => {
    updateData({ rent: value });
  };

  const leaseLengthList = React.useMemo(() => {
    return [
      {
        label: t('lease_length_1'),
        cardLabel: t('years'),
        inputPlaceholder: t('enter'),
        inputValue: data.leaseLengthYears,
        onInputChange: addDate('leaseLengthYears'),
      },
      {
        cardLabel: t('months'),
        inputPlaceholder: t('enter'),
        inputValue: data.leaseLengthMonths,
        onInputChange: addDate('leaseLengthMonths'),
      },
      {
        cardLabel: t('days'),
        inputPlaceholder: t('enter'),
        inputValue: data.leaseLengthDays,
        onInputChange: addDate('leaseLengthDays'),
      },
    ];
  }, [data]);

  return (
    <div className={classes.root}>
      <Flex justifyContent="space-between" className={classes.cardHeader}>
        <span className={classes.cardTitle}>{t('headline_details')}</span>
      </Flex>

      <div className={classes.twoColumnGrid}>
        <DatePicker
          label={t('transaction_date')}
          dateFormat="DD/MM/YYYY"
          error={!!errors?.transactionDate}
          placeholder="DD/MM/YYYY"
          classes={{ picker: classes.datePicker, label: classes.label }}
          value={data.transactionDate || null}
          size={InputSize.medium}
          onChange={(date) => updateData({ transactionDate: date })}
        />
        <SingleSelect
          classes={{ root: classes.rootSelectLeaseType, labelRoot: classes.label, select: classes.select }}
          label={t('lease_type_1')}
          options={lettingTypeOptions}
          placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
          size={ButtonSize.medium}
          value={typeof data.transactionType === 'number' ? data.transactionType.toString() : data.transactionType}
          onChange={(value) => updateData({ transactionType: value.toString() })}
        />
      </div>

      <div className={constants.localisation.currencyDropdownVisible ? classes.twoColumnGrid : classes.oneColumnGrid}>
        {constants.localisation.currencyDropdownVisible && (
          <SingleSelect
            classes={{ root: classes.rootSelect, labelRoot: classes.label, select: classes.select }}
            label={t('currency')}
            placeholder={t('select')}
            fullWidth
            options={currencyOptions}
            size={ButtonSize.medium}
            value={data.rentCurrency}
            onChange={(id) => updateData({ rentCurrency: id.toString() })}
          />
        )}
        <div className={classes.twoColumnGrid}>
          <NumberField
            classes={{ labelError: classes.label, labelRoot: classes.label }}
            error={!!errors?.rent}
            fullWidth
            label={t('headline_rent')}
            InputProps={{
              startAdornment: (
                <span className={classes.currencyAdornment}>
                  {constants.localisation.currencyDefinitions[data.rentCurrency]?.symbol}
                </span>
              ),
            }}
            min={0}
            value={data.rent}
            placeholder={t('enter_rent')}
            size={InputSize.medium}
            onChange={handleChangeEnterRent}
          />
          <SingleSelect
            classes={{ root: classes.rootMetricSelect, labelRoot: classes.label, select: classes.select }}
            label={' '}
            placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
            size={ButtonSize.medium}
            value={data.rentMetric}
            options={rentMetricsOptions}
            onChange={(id: string) => updateData({ rentMetric: id as Metric })}
          />
        </div>
      </div>

      {withRentRates && (
        <div className={classes.fullWidthRentRate}>
          <div className={classes.subheaderText}>
            <span className={classes.label}>{t('rent_rate')}</span>
            <RadioGroup
              options={inclusiveRadioOptions}
              value={data.rentRates}
              serializerType={RadioGroupSerializerType.string}
              classes={{
                formControl: classes.radioGroupControl,
                formControlLabel: classes.radioGroupLabel,
                radios: classes.radioGroup,
              }}
              onChange={(value) => updateData({ rentRates: value.toString() })}
            />
          </div>
        </div>
      )}

      <div className={classes.twoColumnGrid}>
        <DatePicker
          label={t('lease_start')}
          dateFormat="DD/MM/YYYY"
          error={!!errors?.leaseStart}
          placeholder="DD/MM/YYYY"
          classes={{ picker: classes.datePicker, label: classes.label }}
          size={InputSize.medium}
          value={data.leaseStart}
          onChange={(date) => {
            const calculatedEnd = calculateLeaseEnd({ ...data, leaseStart: date });

            updateData({
              leaseStart: date,
              leaseEndReadable: calculatedEnd.dateReadable,
              leaseEnd: calculatedEnd.date,
            });
          }}
        />
        <div className={classes.leaseLength}>
          {leaseLengthList.map(({ cardLabel, label, inputValue, inputPlaceholder, onInputChange }, idx) => (
            <NumberField
              classes={{ labelRoot: classes.label, labelError: classes.label }}
              error={!!errors?.leaseLength}
              InputProps={{ endAdornment: <span className={classes.periodAdormnent}>{cardLabel}</span> }}
              key={idx}
              label={label}
              placeholder={inputPlaceholder}
              size={InputSize.medium}
              fullWidth={false}
              value={inputValue}
              onChange={onInputChange}
            />
          ))}
        </div>
      </div>
      <div className={classes.leaseEndInfo}>{leaseEndDate}</div>

      <div className={classes.divider} />
      <Flex className={classes.itemsLine} justifyContent="flex-start" wrap="nowrap">
        {notInSchedule && (
          <MultiGroupSelect
            classes={{ labelRoot: classes.label, tooltip: classes.notInScheduleSelect }}
            fullWidth
            groupOptions={groupedPreferredSectorOptions}
            label={t('sectors')}
            placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
            size={ButtonSize.medium}
            value={data.sectors}
            onChange={(sectors) => updateData({ sectors: sectors as string[] })}
          />
        )}
        <SingleGroupSelect
          classes={{ labelRoot: classes.label, root: classes.notInScheduleSelect }}
          fullWidth
          groupOptions={groupedPreferredSourceOptions}
          label={t('source')}
          placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
          size={ButtonSize.medium}
          value={data.source}
          onChange={(source) => updateData({ source: source as string })}
        />
      </Flex>
      <div className={classes.divider} />
      {!changeAssignee && data.assignee ? (
        <Flex direction="row">
          <div className={classes.defaultText}>
            {t('transaction_will_be_assigned_to_xnamex', { name: data.assignee.name })}{' '}
          </div>
          <div
            className={classes.changeAssigneeBtn}
            onClick={() => {
              updateData({ assignee: undefined });
              setChangeAssignee(true);
            }}
          >
            [ {t('change_assignee')} ]
          </div>
        </Flex>
      ) : (
        <UserAutocomplete
          dropUp={false}
          label={t('assignee')}
          setUser={(user) => updateData({ assignee: user })}
          classes={{ root: classes.userAutocomplete, label: classes.label }}
        />
      )}
    </div>
  );
};

export const LettingDetailsForm = withStyles(styles)(LettingDetailsFormComponent);
